import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import OnboardingStepper from "./OnboardingStepper";
import PayrollCycleStep from "./steps/PayrollCycleStep";
import DepartmentsStep from "./steps/DepartmentsStep";
import TeamsStep from "./steps/TeamsStep";
import BulkImportStep from "./steps/BulkImportStep";
import DefaultButton from "../buttons/defaultButton";
import toast from "react-hot-toast";
import { createDepartment, getDepartmentList } from "../../../services/workspace/department.service";
import { createDesignation } from "../../../services/workspace/designation.service";
import { createTeam } from "../../../services/workspace/team.service";
import { updateWorkspace, getWorkspaceDetail } from "../../../services/workspace/workspace.service";
import { getRoleList } from "../../../services/workspace/role.service";

const STORAGE_KEY = "onboarding_wizard_state";
const STEPS = [
  { id: 1, label: "Payroll Cycle", key: "payrollCycle" },
  { id: 2, label: "Organization Structure", key: "departments" },
  { id: 3, label: "Teams", key: "teams" },
  { id: 4, label: "Bulk Import", key: "bulkImport" },
];

const OnboardingWizard = ({ onComplete, initialStep = 1 }) => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.user?.userInfo);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [wizardData, setWizardData] = useState({
    payrollCycle: null,
    departments: { departments: [], designations: [] },
    teams: [],
    importJobId: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [savedItems, setSavedItems] = useState({
    departments: [],
    designations: [],
    teams: [],
  });
  const [workspaceData, setWorkspaceData] = useState(null);

  // Load workspace data
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      try {
        const response = await getWorkspaceDetail({});
        const workspace = response?.workspace || response?.data?.workspace || response?.data || response;
        if (workspace && (workspace.uuid || workspace.id)) {
          setWorkspaceData(workspace);
        } else {
          console.warn("Workspace data structure unexpected:", response);
        }
      } catch (error) {
        console.error("Failed to fetch workspace data:", error);
      }
    };
    fetchWorkspaceData();
  }, []);

  // Load persisted state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Handle migration from old format (departments as array) to new format (departments as object)
        if (parsed.data) {
          if (Array.isArray(parsed.data.departments)) {
            parsed.data.departments = { departments: parsed.data.departments, designations: [] };
          }
        }
        setWizardData(parsed.data || wizardData);
        setCurrentStep(parsed.step || initialStep);
      } catch (e) {
        console.error("Failed to load wizard state:", e);
      }
    }
  }, []);

  // Persist state to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ step: currentStep, data: wizardData })
    );
  }, [currentStep, wizardData]);

  const updateWizardData = (stepKey, data) => {
    setWizardData((prev) => ({ ...prev, [stepKey]: data }));
  };

  // Save payroll cycle configuration to workspace
  const savePayrollCycle = async () => {
    if (!wizardData.payrollCycle) {
      return true; // Nothing to save
    }

    setIsSaving(true);
    try {
      // Fetch workspace data if not already loaded
      let workspace = workspaceData;
      if (!workspace?.uuid && !workspace?.id) {
        const response = await getWorkspaceDetail({});
        workspace = response?.workspace || response?.data?.workspace || response?.data || response;
        
        if (workspace && (workspace.uuid || workspace.id)) {
          setWorkspaceData(workspace);
        } else {
          throw new Error("Unable to fetch workspace data. Please refresh the page.");
        }
      }

      const workspaceUuid = workspace?.uuid || workspace?.id;
      if (!workspaceUuid) {
        throw new Error("Workspace UUID not found. Please refresh the page.");
      }

      const { type, day, customDays } = wizardData.payrollCycle;
      
      // Determine payroll_cycle_type and payroll_cycle_value
      let payroll_cycle_type = type;
      let payroll_cycle_value = null;

      if (type === "monthly" || type === "biweekly") {
        payroll_cycle_value = day; // Day number
      } else if (type === "weekly") {
        payroll_cycle_value = day; // Day name (monday, tuesday, etc.)
      } else if (type === "custom") {
        payroll_cycle_value = customDays; // Number of days
      }

      const payload = {
        payroll_cycle_type: payroll_cycle_type,
        payroll_cycle_value: payroll_cycle_value,
      };

      await updateWorkspace(payload, workspaceUuid);
      toast.success("Payroll cycle configuration saved successfully!");
      return true;
    } catch (error) {
      console.error("Error saving payroll cycle:", error);
      const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
      toast.error(`Failed to save payroll cycle: ${errorMsg}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save departments and designations to backend
  // Note: Departments and designations are now saved immediately when added in DepartmentsStep
  // This function is kept for backward compatibility and to handle any unsaved items
  const saveDepartmentsAndDesignations = async () => {
    if (wizardData.departments?.departments?.length === 0) {
      return true; // Nothing to save
    }

    // Check if all departments are already saved
    // Note: Departments and designations are saved immediately when added in DepartmentsStep
    // This function should only handle edge cases where items weren't saved
    // Filter out departments that were already created (even if savedId is missing)
    const unsavedDepartments = wizardData.departments.departments.filter(
      (dept) => !dept.savedId && !dept.isCreated
    );
    // Filter out designations that were already created (even if savedId is missing)
    // Designations are saved immediately when added in DepartmentsStep
    const unsavedDesignations = wizardData.departments.designations.filter(
      (desig) => !desig.savedId && !desig.isCreated
    );

    // If everything is already saved, return success immediately
    // Don't make any API calls if all items have savedId
    if (unsavedDepartments.length === 0 && unsavedDesignations.length === 0) {
      return true;
    }

    setIsSaving(true);
    try {
      // Save any unsaved departments first
      for (const dept of unsavedDepartments) {
        try {
          // Only send name - parent_id and manager_id columns don't exist in database
          const payload = {
            name: dept.name,
            // ...(dept.parentId && { parent_id: dept.parentId }),
            // ...(dept.managerId && { manager_id: dept.managerId }),
          };
          const savedDept = await createDepartment(payload);
          
          // Extract ID from response - handle different response structures
          let departmentId = null;
          if (savedDept) {
            departmentId = savedDept.id || savedDept.uuid || savedDept.data?.id || savedDept.data?.uuid;
          }
          
          if (!departmentId) {
            // Try to fetch from department list as fallback
            try {
              const deptListResponse = await getDepartmentList();
              const deptList = deptListResponse?.department?.data || deptListResponse?.data || deptListResponse || [];
              if (Array.isArray(deptList)) {
                const foundDept = deptList.find(
                  (d) => d.name?.toLowerCase().trim() === dept.name.trim().toLowerCase()
                );
                if (foundDept) {
                  departmentId = foundDept.id || foundDept.uuid;
                }
              }
            } catch (fetchError) {
              console.error("Failed to fetch department list for fallback:", fetchError);
            }
          }
          
          if (departmentId) {
            // Update wizard data with saved department ID
            setWizardData((prev) => ({
              ...prev,
              departments: {
                ...prev.departments,
                departments: prev.departments.departments.map((d) =>
                  d.id === dept.id ? { ...d, savedId: departmentId } : d
                ),
              },
            }));
          } else {
            console.error(`Failed to extract department ID for: ${dept.name}`);
            toast.error(`Department "${dept.name}" created but ID not found. Please refresh the page.`);
          }
        } catch (error) {
          console.error(`Failed to save department ${dept.name}:`, error);
          const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
          toast.error(`Failed to save department: ${dept.name}. ${errorMsg}`);
        }
      }

      // Save any unsaved designations (after departments are saved)
      for (const desig of unsavedDesignations) {
        try {
          // Find the saved department ID
          const dept = wizardData.departments.departments.find(
            (d) => d.id === desig.departmentId
          );
          
          if (!dept) {
            toast.error(`Department not found for designation: ${desig.name}`);
            continue;
          }
          
          let departmentId = dept.savedId;
          
          // If savedId is missing, try to fetch from department list as fallback
          if (!departmentId) {
            try {
              const deptListResponse = await getDepartmentList();
              const deptList = deptListResponse?.department?.data || deptListResponse?.data || deptListResponse || [];
              if (Array.isArray(deptList)) {
                const foundDept = deptList.find(
                  (d) => d.name?.toLowerCase().trim() === dept.name.trim().toLowerCase()
                );
                if (foundDept) {
                  departmentId = foundDept.id || foundDept.uuid;
                }
              }
            } catch (fetchError) {
              console.error("Failed to fetch department list for fallback:", fetchError);
            }
          }
          
          if (!departmentId) {
            toast.error(`Department ID not available for designation: ${desig.name}. Please ensure the department was saved successfully.`);
            continue;
          }

          // Fetch roles - required field for designation
          let roles = [];
          try {
            const rolesResponse = await getRoleList({});
            const rolesData = rolesResponse?.role?.data || rolesResponse?.data || [];
            // Extract role IDs - roles should be an array of IDs
            roles = Array.isArray(rolesData) ? rolesData.map((role) => role.id || role.uuid).filter(Boolean) : [];
          } catch (rolesError) {
            console.warn("Failed to fetch roles for designation:", rolesError);
            // Continue with empty array - backend will validate
          }

          const payload = {
            name: desig.name,
            abrv: desig.abrv || "",
            department_id: departmentId,
            description: desig.description || "",
            roles: roles, // Array of role IDs (required field)
          };
          
          const savedDesig = await createDesignation(payload);
          
          // Extract designation ID from response
          let designationId = null;
          if (savedDesig) {
            designationId = savedDesig.id || savedDesig.uuid || savedDesig.data?.id || savedDesig.data?.uuid;
          }
          
          if (designationId) {
            // Update wizard data with saved designation ID
            setWizardData((prev) => ({
              ...prev,
              departments: {
                ...prev.departments,
                designations: prev.departments.designations.map((d) =>
                  d.id === desig.id ? { ...d, savedId: designationId } : d
                ),
              },
            }));
          } else {
            console.warn(`Designation ID not found in response for: ${desig.name}`);
          }
        } catch (error) {
          console.error(`Failed to save designation ${desig.name}:`, error);
          const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
          toast.error(`Failed to save designation: ${desig.name}. ${errorMsg}`);
        }
      }

      return true;
    } catch (error) {
      console.error("Error saving departments/designations:", error);
      toast.error("Failed to save organization structure");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save teams to backend
  const saveTeams = async () => {
    if (wizardData.teams?.length === 0) {
      return true; // Nothing to save
    }

    setIsSaving(true);
    try {
      const teamsToSave = wizardData.teams.filter(
        (team) => !savedItems.teams.includes(team.id)
      );

      for (const team of teamsToSave) {
        try {
          // Find the saved department ID
          const dept = wizardData.departments.departments.find(
            (d) => d.id === team.departmentId
          );
          
          if (!dept) {
            toast.error(`Department not found for team: ${team.name}`);
            continue;
          }

          let departmentId = dept.savedId;
          
          // If savedId is missing, try to fetch from department list as fallback
          if (!departmentId) {
            try {
              // Fetch department list and find the department by name
              const deptListResponse = await getDepartmentList();
              const deptList = deptListResponse?.department?.data || deptListResponse?.data || deptListResponse || [];
              
              if (Array.isArray(deptList)) {
                const foundDept = deptList.find(
                  (d) => d.name?.toLowerCase().trim() === dept.name.trim().toLowerCase()
                );
                
                if (foundDept) {
                  departmentId = foundDept.id || foundDept.uuid;
                }
              }
            } catch (fetchError) {
              console.error("Failed to fetch department list for fallback:", fetchError);
            }
          }
          
          // If still no department ID, skip this team
          if (!departmentId) {
            toast.error(`Department ID not available for team: ${team.name}. Please ensure the department was saved successfully.`);
            continue;
          }

          // Build payload
          // Note: manager_id is optional - only include if leadId is provided
          const payload = {
            name: team.name,
            department_id: departmentId, // Use backend department ID
            ...(team.leadId && { manager_id: team.leadId }), // Optional field (team lead is stored as manager_id)
          };
          
          const savedTeam = await createTeam(payload);
          
          setSavedItems((prev) => ({
            ...prev,
            teams: [...prev.teams, team.id],
          }));
        } catch (error) {
          console.error(`Failed to save team ${team.name}:`, error);
          const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
          toast.error(`Failed to save team: ${team.name}. ${errorMsg}`);
        }
      }

      return true;
    } catch (error) {
      console.error("Error saving teams:", error);
      toast.error("Failed to save teams");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    // Save data before moving to next step
    if (currentStep === 1) {
      // Save payroll cycle configuration before moving to departments step
      const saved = await savePayrollCycle();
      if (!saved) {
        return; // Don't proceed if save failed
      }
    } else if (currentStep === 2) {
      // Save departments and designations before moving to teams step
      const saved = await saveDepartmentsAndDesignations();
      if (!saved) {
        return; // Don't proceed if save failed
      }
    } else if (currentStep === 3) {
      // Save teams before moving to bulk import step
      const saved = await saveTeams();
      if (!saved) {
        return; // Don't proceed if save failed
      }
    }

    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleComplete = async () => {
    // Save any remaining data before completing
    if (currentStep === 1) {
      await savePayrollCycle();
    } else if (currentStep === 2) {
      await saveDepartmentsAndDesignations();
    } else if (currentStep === 3) {
      await saveTeams();
    }

    localStorage.removeItem(STORAGE_KEY);
    toast.success("Onboarding completed successfully!");
    if (onComplete) {
      onComplete(wizardData);
    } else {
      navigate("/hr/staff-directory");
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!wizardData.payrollCycle;
      case 2:
        return wizardData.departments?.departments?.length > 0;
      case 3:
        return wizardData.teams.length > 0;
      case 4:
        return true; // Bulk import is optional
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PayrollCycleStep
            value={wizardData.payrollCycle}
            onChange={(data) => updateWizardData("payrollCycle", data)}
          />
        );
      case 2:
        return (
          <DepartmentsStep
            value={wizardData.departments}
            onChange={(data) => updateWizardData("departments", data)}
            payrollCycle={wizardData.payrollCycle}
          />
        );
      case 3:
        return (
          <TeamsStep
            value={wizardData.teams}
            onChange={(data) => updateWizardData("teams", data)}
            departments={wizardData.departments?.departments || []}
          />
        );
      case 4:
        return (
          <BulkImportStep
            onImportComplete={(jobId) =>
              updateWizardData("importJobId", jobId)
            }
            importJobId={wizardData.importJobId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-100px)] bg-white -p-4">
      {/* Header */}
      <div className="bg-white border-b border-borderColor shadow-sm">
        <div className=" mx-auto py-4">
          <h1 className="text-2xl font-semibold text-theme">
            HRMS Onboarding Wizard
          </h1>
          <p className="text-sm text-textLight mt-1">
            Set up your organization structure and import employees
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white border-b border-borderColor">
        <div className=" mx-auto py-4">
          <OnboardingStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1  mx-auto bg-white w-full py-4">
        <div className="bg-white rounded-lg shadow-sm border border-borderColor p-6">
          {renderStepContent()}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="bg-white border-t border-borderColor shadow-sm">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <DefaultButton
              variant="secondary"
              name="Previous"
              minWidth
              onClick={handlePrevious}
              disabled={currentStep === 1}
            />
            <div className="flex gap-3">
              {currentStep < STEPS.length ? (
                <DefaultButton
                  variant="default"
                  name={isSaving ? "Saving..." : "Next"}
                  onClick={handleNext}
                  minWidth
                  disabled={!canProceed() || isSaving}
                />
              ) : (
                <DefaultButton
                  variant="success"
                  name={isSaving ? "Completing..." : "Complete Setup"}
                  onClick={handleComplete}
                  disabled={isSaving}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;

